import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const labels = {
  direct_first_message: "Primeiro Direct recebido",
  story_reply: "Resposta a um story",
  comment_keyword: "Comentário com palavra-chave",
};

const emptyComment = {
  mediaId: "",
  keyword: "",
  publicReply: "",
  privateMessage: "",
  link: "",
};

const InstagramTriggerModal = ({ open, type, flow, onClose, onSaved }) => {
  const [selectedType, setSelectedType] = useState("direct_first_message");
  const [form, setForm] = useState(emptyComment);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const set = (key, value) => setForm(previous => ({ ...previous, [key]: value }));

  useEffect(() => {
    if (!open) return;
    const initialType = type === "instagram_start"
      ? (flow?.triggerConfig?.type !== "manual" ? flow?.triggerConfig?.type : "direct_first_message")
      : type;
    setSelectedType(initialType);
    const current = flow?.triggerConfig?.type === initialType ? flow.triggerConfig : {};
    setForm({ ...emptyComment, ...current });
    setPosts([]);
  }, [open, type, flow]);

  useEffect(() => {
    if (!open || selectedType !== "comment_keyword" || !flow?.connectionId || posts.length) return;
    setLoadingPosts(true);
    api.get(`/flowbuilder/instagram-media/${flow.connectionId}`)
      .then(({ data }) => setPosts(data.posts || []))
      .catch(toastError)
      .finally(() => setLoadingPosts(false));
  }, [open, selectedType, flow?.connectionId, posts.length]);

  const save = async () => {
    const effectiveType = selectedType;
    if (effectiveType === "comment_keyword" && (!form.mediaId || !form.keyword.trim() || !form.privateMessage.trim() || !form.link.trim())) {
      toast.error("Selecione o post e informe a palavra-chave, a mensagem e o link do Direct.");
      return;
    }
    const triggerConfig = effectiveType === "comment_keyword"
      ? {
          type: effectiveType,
          mediaId: form.mediaId,
          keyword: form.keyword.trim(),
          publicReply: form.publicReply.trim(),
          privateMessage: form.privateMessage.trim(),
          link: form.link.trim(),
        }
      : { type: effectiveType };
    setLoading(true);
    try {
      await api.put("/flowbuilder", {
        flowId: flow.id,
        name: flow.name,
        connectionId: Number(flow.connectionId),
        triggerConfig,
      });
      toast.success(`${labels[effectiveType]} configurado no início do fluxo.`);
      onSaved(triggerConfig);
      onClose();
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle>{type === "instagram_start" ? "Início do Instagram" : labels[selectedType] || "Gatilho do Instagram"}</DialogTitle>
      <DialogContent dividers>
        {type === "instagram_start" && (
          <TextField select fullWidth margin="normal" variant="outlined" label="Como iniciar o fluxo"
            value={selectedType} onChange={event => setSelectedType(event.target.value)}>
            <MenuItem value="direct_first_message">Primeiro Direct recebido</MenuItem>
            <MenuItem value="story_reply">Resposta a um story</MenuItem>
            <MenuItem value="comment_keyword">Comentário com palavra-chave em um post</MenuItem>
          </TextField>
        )}
        {selectedType === "direct_first_message" && (
          <Typography variant="body2">
            O fluxo será iniciado quando a pessoa enviar a primeira mensagem para o Direct desta conta.
          </Typography>
        )}
        {selectedType === "story_reply" && (
          <Typography variant="body2">
            O fluxo será iniciado quando a pessoa responder a um story desta conta pelo Direct.
          </Typography>
        )}
        {selectedType === "comment_keyword" && (
          <>
            <TextField
              select fullWidth margin="normal" variant="outlined"
              label="Post ou reel" value={form.mediaId}
              disabled={loadingPosts}
              onChange={event => set("mediaId", event.target.value)}
              helperText={loadingPosts ? "Carregando publicações..." : "A regra será aplicada somente à publicação escolhida."}
            >
              {posts.map(post => (
                <MenuItem key={post.id} value={post.id}>
                  {post.media_type === "VIDEO" ? "Reel" : "Post"} — {(post.caption || post.permalink || post.id).slice(0, 75)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth margin="normal" variant="outlined"
              label="Palavra-chave do comentário" value={form.keyword}
              onChange={event => set("keyword", event.target.value)}
              helperText="A palavra pode estar em qualquer parte do comentário."
            />
            <TextField
              fullWidth margin="normal" variant="outlined"
              label="Resposta pública (opcional)" value={form.publicReply}
              onChange={event => set("publicReply", event.target.value)}
            />
            <TextField
              fullWidth multiline rows={3} margin="normal" variant="outlined"
              label="Mensagem privada no Direct" value={form.privateMessage}
              onChange={event => set("privateMessage", event.target.value)}
            />
            <TextField
              fullWidth margin="normal" variant="outlined"
              label="Link HTTPS a enviar" value={form.link}
              onChange={event => set("link", event.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={save} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Usar este gatilho"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstagramTriggerModal;
